import React, {
	useEffect,
	useState,
	useRef,
	forwardRef,
	useImperativeHandle
} from 'react';
import ReactMapboxGl, { Layer, Source } from 'react-mapbox-gl';
import config from 'config';
import { MapLayer } from './map/MapLayer';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { mapBoxDrawnLayerStyle } from 'components/modals/modalHelpers';
import bbox from '@turf/bbox';

const Map = ReactMapboxGl({
	accessToken: config.mapboxToken
});

export const AppMap = forwardRef((props, ref) => {
	const {
		layers = [],
		center = [38, 14],
		zoom = 6,
		bounds,
		draw = false,
		initialDrawFeature,
		onFeatureClick,
		onFeatureMouseEnter,
		onFeatureMouseLeave,
		onDrawCreate,
		onDrawUpdate,
		onDrawDelete,
		tileLayer
	} = props;

	const mapRef = useRef(null);
	const drawRef = useRef(null);
	const initialCenterRef = useRef(center);
	const initialZoomRef = useRef([zoom]);

	const latLongRef = useRef(null);
	const [drawReady, setDrawReady] = useState(false);

	useImperativeHandle(ref, () => ({
		recenter: (center, zoom) => {
			if (!mapRef.current) return;

			mapRef.current.flyTo({
				center,
				...(zoom != null && { zoom })
			});
		},

		fitBounds: (bounds, options = {}) => {
			if (!mapRef.current) return;

			mapRef.current.fitBounds(bounds, {
				padding: 50,
				...options
			});
		},

		addDrawFeature: (feature) => {
			if (!drawRef.current || !feature) return;

			return drawRef.current.add(feature);
		},

		deleteAllDrawFeatures: () => {
			if (!drawRef.current) return;

			drawRef.current.deleteAll();
		},

		setDrawFeature: (feature) => {
			if (!drawRef.current || !feature) return;

			drawRef.current.deleteAll();

			return drawRef.current.add(feature);
		},

		resetDrawFeature: (feature) => {
			console.log(feature)
			if (!drawRef.current || !feature) return;

			drawRef.current.set({
				type: 'FeatureCollection',
				features: [feature]
			});
		}
	}));

	const useLatest = (value) => {
		const ref = useRef(value);

		useEffect(() => {
			ref.current = value;
		}, [value]);

		return ref;
	};

	const onDrawCreateRef = useLatest(onDrawCreate);
	const onDrawUpdateRef = useLatest(onDrawUpdate);
	const onDrawDeleteRef = useLatest(onDrawDelete);

	const handleStyleLoad = (map) => {
		mapRef.current = map;

		if (bounds) {
			map.fitBounds(bounds, {
				padding: 50
			});
		}

		if (draw && !drawRef.current) {
			drawRef.current = new MapboxDraw({
				displayControlsDefault: false,
				controls: {
					polygon: true,
					trash: true
				},
				styles: mapBoxDrawnLayerStyle
			});

			map.addControl(drawRef.current, 'top-right');

			map.on('draw.create', (e) => {
				if (onDrawCreateRef.current) {
					onDrawCreateRef.current(e.features);
				}
			});

			map.on('draw.update', (e) => {
				if (onDrawUpdateRef.current) {
					onDrawUpdateRef.current(e.features);
				}
			});

			map.on('draw.delete', (e) => {
				if (onDrawDeleteRef.current) {
					onDrawDeleteRef.current(e.features);
				}
			});

			setDrawReady(true);
		}
	};

	useEffect(() => {
		if (
			!drawReady ||
			!initialDrawFeature ||
			!drawRef.current ||
			!mapRef.current
		) {
			return;
		}

		drawRef.current.deleteAll();
		drawRef.current.add(initialDrawFeature);

		const [minLng, minLat, maxLng, maxLat] = bbox(initialDrawFeature);

		mapRef.current.fitBounds(
			[
				[minLng, minLat],
				[maxLng, maxLat]
			],
			{
				padding: 50,
				maxZoom: 15
			}
		);
	}, [drawReady, initialDrawFeature?.id]);

	return (
		<>
			<div ref={latLongRef} id="latlonginfo">
				Lng: ---, Lat: ---
			</div>
			<Map
				style={`mapbox://styles/mapbox/${tileLayer}`}
				center={initialCenterRef.current}
				zoom={initialZoomRef.current}
				onStyleLoad={handleStyleLoad}
				onMouseMove={(map, e) => {
					const { lng, lat } = e.lngLat;

					if (latLongRef.current) {
						latLongRef.current.textContent = `Lng: ${lng.toFixed(3)}, Lat: ${lat.toFixed(3)}`;
					}
				}}
				containerStyle={{
					width: '100%',
					height: '100%'
				}}
			>
				{layers.map((layer) => (
					<MapLayer
						mapRef={mapRef}
						key={layer.id}
						layer={layer}
						onFeatureClick={onFeatureClick}
						onFeatureMouseEnter={onFeatureMouseEnter}
						onFeatureMouseLeave={onFeatureMouseLeave}
					/>
				))}
			</Map>
		</>
	);
});
